import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ChevronLeft,
  AlertCircle,
  CheckCircle,
  Loader,
  Mail,
  ShieldCheck
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { SERVICE_CATEGORIES, SERVICE_PROVIDERS } from '../../lib/serviceProviders'

export default function ProviderClaim() {
  const { providerId } = useParams()
  const navigate = useNavigate()

  const [isExisting, setIsExisting] = useState(false)
  const [existingProvider, setExistingProvider] = useState(null)
  const [loading, setLoading] = useState(providerId ? true : false)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  // Form state for new listing
  const [formData, setFormData] = useState({
    business_name: '',
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    category: '',
    location: '',
    description: '',
    business_email: ''
  })

  // Form state for existing claim
  const [claimData, setClaimData] = useState({
    requester_name: '',
    business_email: '',
    requester_phone: ''
  })

  // Extract domain from provider website for email hint
  const getExpectedDomain = () => {
    if (!existingProvider?.website) return null
    try {
      const url = existingProvider.website.startsWith('http')
        ? existingProvider.website
        : `https://${existingProvider.website}`
      return new URL(url).hostname.replace('www.', '')
    } catch {
      return null
    }
  }

  // Fetch existing provider if providerId is provided
  useEffect(() => {
    if (providerId) {
      fetchExistingProvider()
    }
  }, [providerId])

  const fetchExistingProvider = async () => {
    try {
      setLoading(true)
      setError('')

      let found = null

      // First try DB lookup by UUID
      try {
        const { data, error: fetchError } = await supabase
          .from('service_providers')
          .select('*')
          .eq('id', providerId)
          .single()

        if (data && !fetchError) {
          found = data
        }
      } catch (e) {
        // DB table may not exist yet — that's fine
      }

      // If not found by UUID, try DB lookup by slug
      if (!found) {
        try {
          const { data, error: fetchError } = await supabase
            .from('service_providers')
            .select('*')
            .eq('slug', providerId)
            .single()

          if (data && !fetchError) {
            found = data
          }
        } catch (e) {
          // DB table may not exist yet — that's fine
        }
      }

      // If still not found, look up in static data
      if (!found) {
        const staticProvider = SERVICE_PROVIDERS.find(p => p.id === providerId)
        if (staticProvider) {
          found = {
            id: staticProvider.id,
            name: staticProvider.name,
            category: staticProvider.category,
            location: staticProvider.location,
            region: staticProvider.region,
            description: staticProvider.description,
            phone: staticProvider.phone,
            website: staticProvider.website,
            rating: staticProvider.rating,
            specialties: staticProvider.specialties,
            services: staticProvider.services,
            price_range: staticProvider.priceRange,
            _fromStatic: true
          }
        }
      }

      if (found) {
        setExistingProvider(found)
        setIsExisting(true)
      } else {
        // Provider ID doesn't match anything — still allow a claim attempt
        setIsExisting(true)
        setExistingProvider({ id: providerId })
      }
    } catch (err) {
      console.error('Error fetching provider:', err)
      setError('Error loading provider information. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleNewListingChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleClaimChange = (e) => {
    const { name, value } = e.target
    setClaimData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const validatePhone = (phone) => {
    return /^[\d\s\-\+\(\)]{10,}$/.test(phone.replace(/\s/g, ''))
  }

  const submitNewListing = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!formData.business_name.trim()) {
      setError('Business name is required')
      return
    }
    if (!formData.requester_name.trim()) {
      setError('Your name is required')
      return
    }
    if (!validatePhone(formData.requester_phone)) {
      setError('Please enter a valid phone number')
      return
    }
    if (!formData.category) {
      setError('Please select a service category')
      return
    }
    if (!formData.location.trim()) {
      setError('Business location is required')
      return
    }
    if (!formData.description.trim()) {
      setError('Business description is required')
      return
    }
    if (!validateEmail(formData.business_email)) {
      setError('Please enter a valid business email address')
      return
    }

    try {
      setSubmitting(true)

      const response = await fetch('/api/providers/verify-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: formData.business_name,
          business_email: formData.business_email,
          requester_name: formData.requester_name,
          requester_phone: formData.requester_phone,
          provider_id: null
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to send verification email')

      setSuccess(true)
    } catch (err) {
      console.error('Error submitting claim:', err)
      setError(err.message || 'Failed to submit your request. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const submitExistingClaim = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!claimData.requester_name.trim()) {
      setError('Your name is required')
      return
    }
    if (!validateEmail(claimData.business_email)) {
      setError('Please enter a valid business email address')
      return
    }
    if (!validatePhone(claimData.requester_phone)) {
      setError('Please enter a valid phone number')
      return
    }

    try {
      setSubmitting(true)

      // Call the verification email API endpoint
      const response = await fetch('/api/providers/verify-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: existingProvider?.name || providerId,
          business_email: claimData.business_email,
          requester_name: claimData.requester_name,
          requester_phone: claimData.requester_phone,
          provider_id: existingProvider?._fromStatic ? null : (existingProvider?.id || null)
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to send verification email')

      setSuccess(true)
    } catch (err) {
      console.error('Error submitting claim:', err)
      setError(err.message || 'Failed to send verification email. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader size={32} className="animate-spin text-rdc-orange mx-auto mb-3" />
          <p className="text-rdc-taupe">Loading provider information...</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="animate-fade-in max-w-2xl mx-auto">
        <Link to="/specialists" className="flex items-center gap-2 text-rdc-orange hover:text-rdc-orange/80 mb-8 font-medium">
          <ChevronLeft size={18} />
          Back to Specialists
        </Link>

        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-rdc-forest/10 flex items-center justify-center">
            <CheckCircle size={32} className="text-rdc-forest" />
          </div>

          <h1 className="page-title mb-3">Verification Email Sent</h1>

          <p className="text-body text-rdc-charcoal mb-6 max-w-lg mx-auto">
            We've sent a verification email to the business email address you provided. Please check your inbox and click the verification link to confirm ownership.
          </p>

          <p className="text-sm text-rdc-taupe mb-8">
            Didn't receive the email? Check your spam folder, or contact us at support@ridinganddrivingclub.com.
          </p>

          <Link to="/specialists" className="btn-primary inline-block">
            Return to Specialists
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      {/* Back Link */}
      <Link to="/specialists" className="flex items-center gap-2 text-rdc-orange hover:text-rdc-orange/80 mb-8 font-medium">
        <ChevronLeft size={18} />
        Back to Specialists
      </Link>

      {/* Header */}
      <div className="mb-8">
        <h1 className="page-title">
          {isExisting && existingProvider ? 'Claim Listing' : 'List Your Business'}
        </h1>
        <p className="text-body mt-2">
          {isExisting && existingProvider
            ? `Verify your ownership of ${existingProvider.name || 'this service provider'}`
            : 'Join our directory and connect with collectors and enthusiasts'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Existing Provider Header */}
      {isExisting && existingProvider && (
        <div className="card p-6 mb-8 bg-rdc-cream/50 border border-rdc-orange/20">
          <h2 className="section-title mb-1">{existingProvider.name || 'Service Provider'}</h2>
          {existingProvider.location && (
            <p className="text-sm text-rdc-taupe mb-2">{existingProvider.location}</p>
          )}
          {existingProvider.description && (
            <p className="text-sm text-rdc-charcoal mt-3">{existingProvider.description}</p>
          )}
          {existingProvider.specialties && existingProvider.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {existingProvider.specialties.slice(0, 5).map((s, i) => (
                <span key={i} className="px-2 py-0.5 bg-rdc-stone/20 text-rdc-charcoal text-xs rounded-full">{s}</span>
              ))}
            </div>
          )}
          <p className="text-xs text-rdc-taupe mt-4 italic">
            Claim this listing to manage and update the information shown above.
          </p>
        </div>
      )}

      {/* Form */}
      <div className="card p-8">
        <form onSubmit={isExisting ? submitExistingClaim : submitNewListing}>
          {/* New Listing Form */}
          {!isExisting && (
            <>
              {/* Business Name */}
              <div className="mb-6">
                <label className="form-label">Business Name *</label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleNewListingChange}
                  placeholder="e.g., Repasi Motorwerks"
                  className="input-field"
                />
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="form-label">Business Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleNewListingChange}
                  placeholder="Describe your services, expertise, and what makes your business special"
                  rows="4"
                  className="input-field resize-none"
                />
              </div>

              {/* Category */}
              <div className="mb-6">
                <label className="form-label">Service Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleNewListingChange}
                  className="input-field"
                >
                  <option value="">Select a category</option>
                  {SERVICE_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div className="mb-6">
                <label className="form-label">Business Location *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleNewListingChange}
                  placeholder="City, State"
                  className="input-field"
                />
              </div>
            </>
          )}

          {/* Requester Info */}
          <div className={!isExisting ? 'mb-8 pt-8 border-t border-rdc-stone/30' : ''}>
            {!isExisting && <p className="text-label mb-6">Your Information</p>}

            <div className="mb-6">
              <label className="form-label">Your Name *</label>
              <input
                type="text"
                name="requester_name"
                value={isExisting ? claimData.requester_name : formData.requester_name}
                onChange={isExisting ? handleClaimChange : handleNewListingChange}
                placeholder="Full name"
                className="input-field"
              />
            </div>

            <div className="mb-6">
              <label className="form-label">Phone Number *</label>
              <input
                type="tel"
                name="requester_phone"
                value={isExisting ? claimData.requester_phone : formData.requester_phone}
                onChange={isExisting ? handleClaimChange : handleNewListingChange}
                placeholder="(555) 123-4567"
                className="input-field"
              />
            </div>
          </div>

          {/* Business Email Verification */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-rdc-forest" />
              <label className="form-label mb-0">Business Email Verification *</label>
            </div>
            <p className="text-sm text-rdc-charcoal mb-4">
              Enter your business email address. We'll send a verification link to confirm you have access to this business's email.
            </p>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rdc-taupe" />
              <input
                type="email"
                name="business_email"
                value={isExisting ? claimData.business_email : formData.business_email}
                onChange={isExisting ? handleClaimChange : handleNewListingChange}
                placeholder={
                  isExisting && getExpectedDomain()
                    ? `e.g., info@${getExpectedDomain()}`
                    : 'e.g., info@yourbusiness.com'
                }
                className="input-field pl-10"
              />
            </div>
            {isExisting && getExpectedDomain() && (
              <p className="text-xs text-rdc-taupe mt-2">
                Use an email address associated with <span className="font-medium">{getExpectedDomain()}</span> for fastest verification.
              </p>
            )}
            {!isExisting && (
              <p className="text-xs text-rdc-taupe mt-2">
                Use an official business email (not a personal address like Gmail or Yahoo) for fastest verification.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {submitting && <Loader size={16} className="animate-spin" />}
            {submitting ? 'Sending Verification...' : 'Send Verification Email'}
          </button>
        </form>

        {/* Terms Notice */}
        <p className="text-xs text-rdc-taupe text-center mt-6">
          We'll send a verification email to confirm you have access to this business email. By proceeding, you confirm that you own or are authorized to manage this business.
        </p>
      </div>
    </div>
  )
}
